import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Col, Form, Row } from "reactstrap";
import { useIntl } from "react-intl";
import { useMeCore } from "../../../cores/me";
import { CardTag } from "../../../cores/boards/interfaces/api/get-tags";
import { InputWembii } from "../InputWembii";
import { useContactCore } from "../../../cores/contacts";
import { CONTACT_TAGS } from "../../../constants/internalModal.constant";
import { useGlobalCore } from "../../../cores/globals";

interface Props {
  tag?: CardTag;
  onUpdate: () => void;
}

export default function TagForm({ tag, onUpdate }: Props) { 
  const intl = useIntl();
  const { company } = useMeCore();
  const { turnOffInternalModal } = useGlobalCore();
  const { postContactTag, putContactTag, deleteContactTag } = useContactCore();

  const [name, setName] = useState("");
  const [textColor, setTextColor] = useState("#fff");
  const [backgroundColor, setBackgroundColor] = useState("#000");

  useEffect(() => {
    if (tag) {
      setTextColor(tag.textColor);
      setBackgroundColor(tag.backgroundColor);
      setName(tag.name);
    }
  }, [tag]);

  const cleanForm = useCallback(() => {
    setTextColor("#fff");
    setBackgroundColor("#000");
    setName("");
    turnOffInternalModal(CONTACT_TAGS);
  }, [turnOffInternalModal]);

  const onSubmit = (e: any) => {
    e.preventDefault();

    if (!tag) {
      postContactTag(
        company?.id!,
        name,
        textColor,
        backgroundColor,
        cleanForm
      );
    } else {
      putContactTag(
        company?.id!,
        tag.id,
        name,
        textColor,
        backgroundColor,
        () => {
          cleanForm();
          onUpdate();
        }
      );
    }
  };

  const onDelete = useCallback(() => {
    deleteContactTag(
      company?.id!,
      tag?.id!,
      cleanForm
    );
  }, [deleteContactTag, cleanForm, company, tag]);

  const disabled = useCallback(
    () => !name || !textColor || !backgroundColor,
    [name, textColor, backgroundColor]
  );

  return (
    <Form className="p-2" onSubmit={onSubmit}>
      <Row>
        <Col>
          <InputWembii
            label={intl.formatMessage({ id: "app.contacts.origins.textColor" })}
            type="color"
            name="color"
            id="tag-color"
            className="px-1"
            required
            placeholder={intl.formatMessage({ id: "app.contacts.origins.textColor" })}
            value={textColor}
            onChange={setTextColor}
          />
          <InputWembii
            label={intl.formatMessage({ id: "app.contacts.origins.backgroundColor" })}
            type="color"
            name="color"
            id="tag-color"
            className="pl-1"
            required
            placeholder={intl.formatMessage({ id: "app.contacts.origins.backgroundColor" })}
            value={backgroundColor}
            onChange={setBackgroundColor}
          />
        </Col>
        <Col>
          <InputWembii
            label={intl.formatMessage({ id: "app.contacts.origins.name" })}
            type="text"
            name="name"
            id="tag-name"
            className="pr-1"
            required
            placeholder={intl.formatMessage({ id: "app.contacts.origins.name" })}
            value={name}
            onChange={setName}
          />
          {name && (
            <Badge pill className="my-2" style={{ backgroundColor: backgroundColor }}>
              <div className="px-2 py-1">
                <span className="h6 mb-0 font-weight-bold" style={{ color: textColor }}>
                  {name}
                </span>
              </div>
            </Badge>
          )}
        </Col>
      </Row>

      {tag && (
        <Button color="danger" className="mr-1" onClick={onDelete}>
          {intl.formatMessage({ id: "app.contacts.origins.delete" })}  
        </Button>
      )}
      <Button color="primary" disabled={disabled()} className="ml-1">
        {tag 
          ? intl.formatMessage({ id: "app.contacts.origins.update" })
          : intl.formatMessage({ id: "app.contacts.origins.create" })
        }  
      </Button>
    </Form>
  );
}